import { PDFDocument, PDFPage } from "pdf-lib"
import { cropFill } from "@services/pdf/assetLoader"
import { pageHeight, pageWidth, pdfCreator, poweredByUrl } from "@services/pdf/constants"
import { IFontSet } from "@services/pdf/fontSet"
import { black, drawRuns, IRun, runsFromText, white } from "@services/pdf/textLayout"
import { TPdfLayoutType } from "@services/pdf/types"
import {
	addLinkAnnotation,
	drawImageInTopRect,
	drawTemplateBackground,
	drawWhiteBackground,
	embedImage,
} from "./shared"

export interface ICoverStrings {
	confidentialTop: string // "PRIVATE AND CONFIDENTIAL"
	confidentialBottom: string // long confidential paragraph
	poweredBy: string // "Powered by {{creator}}" already interpolated EXCEPT creator ("Powered by %@")
}

/**
 * Port of iOS WorkPDFGenerator.drawViewsCover + the cover link annotation.
 * Used when the org has no cover template.
 */
export function drawViewsCover(
	doc: PDFDocument,
	page: PDFPage,
	title: string,
	layoutType: TPdfLayoutType,
	fonts: IFontSet,
	strings: ICoverStrings
): void {
	drawWhiteBackground(page)

	const width = pageWidth
	const height = pageHeight

	const titleFont = fonts.coverFont(layoutType)
	const titleSize = fonts.coverFontSize(layoutType)
	const detailFamily = fonts.contentFamily(layoutType)

	if (title !== "") {
		const sideEdge = 0.1 * width
		drawRuns(
			page,
			runsFromText(title, {
				font: titleFont,
				size: titleSize,
				lineSpacing: 4,
				align: "center",
			}),
			{ x: sideEdge, yTop: sideEdge, width: width - sideEdge * 2, height: height - sideEdge * 2 },
			"center"
		)
	}

	// top confidential — Bold 14, left, top (iOS rect x: 0.083w, y: 0.059h)
	drawRuns(
		page,
		runsFromText(strings.confidentialTop, {
			font: fonts.resolve(detailFamily, "Bold"),
			size: 14,
			lineSpacing: 4,
			align: "left",
		}),
		{ x: width * 0.083, yTop: height * 0.059, width: Number.MAX_SAFE_INTEGER, height: Number.MAX_SAFE_INTEGER },
		"top"
	)

	// bottom confidential — Regular 12, wrapped at measuredWidth / 2.7
	const confidentialStyle = {
		font: fonts.resolve(detailFamily, "Regular"),
		size: 12,
		lineSpacing: 4,
		align: "left" as const,
	}
	const fullWidth = confidentialStyle.font.widthOfTextAtSize(strings.confidentialBottom, 12)
	drawRuns(
		page,
		runsFromText(strings.confidentialBottom, confidentialStyle),
		{ x: width * 0.083, yTop: height * 0.847, width: fullWidth / 2.7, height: Number.MAX_SAFE_INTEGER },
		"top"
	)

	// brand — "Powered by Views" with Views bold + underlined
	const poweredByText = strings.poweredBy.replace("{{creator}}", pdfCreator)
	const creatorIdx = poweredByText.indexOf(pdfCreator)
	const regular = {
		font: fonts.resolve(detailFamily, "Regular"),
		size: 12,
		lineSpacing: 4,
		align: "left" as const,
	}
	const bold = {
		font: fonts.resolve(detailFamily, "Bold"),
		size: 12,
		lineSpacing: 4,
		align: "left" as const,
		underline: true,
	}
	let brandRuns: IRun[]
	if (creatorIdx >= 0) {
		brandRuns = [
			...runsFromText(poweredByText.slice(0, creatorIdx), regular),
			...runsFromText(pdfCreator, bold),
			...runsFromText(poweredByText.slice(creatorIdx + pdfCreator.length), regular),
		]
	} else {
		brandRuns = runsFromText(poweredByText, regular)
	}
	drawRuns(
		page,
		brandRuns,
		{ x: width * 0.083, yTop: height * 0.922, width: Number.MAX_SAFE_INTEGER, height: Number.MAX_SAFE_INTEGER },
		"top"
	)

	// link annotation — iOS: CGRect(x: 0.083w, y: h - 0.922h - 30, width: 125, height: 45), PDF coords
	addLinkAnnotation(
		doc,
		page,
		{ x: width * 0.083, y: height - height * 0.922 - 30, width: 125, height: 45 },
		poweredByUrl
	)
}

/**
 * Port of iOS drawOrgCover: org template background + centered title.
 * Link annotation rect differs from the Views cover (iOS: 310,25,35,35 / wc 35x20).
 */
export async function drawOrgCover(
	doc: PDFDocument,
	page: PDFPage,
	title: string,
	templateBytes: Uint8Array,
	layoutType: TPdfLayoutType,
	fonts: IFontSet,
	options?: { wcLinkRect?: boolean }
): Promise<void> {
	drawWhiteBackground(page)
	await drawTemplateBackground(doc, page, templateBytes)

	const width = pageWidth
	const height = pageHeight
	const sideEdge = 0.1 * width

	if (title !== "") {
		drawRuns(
			page,
			runsFromText(title, {
				font: fonts.coverFont(layoutType),
				size: fonts.coverFontSize(layoutType),
				lineSpacing: 4,
				align: "center",
				color: layoutType === "gould" ? white : black,
			}),
			{ x: sideEdge, yTop: sideEdge, width: width - sideEdge * 2, height: height - sideEdge * 2 },
			"center"
		)
	}

	addLinkAnnotation(doc, page, { x: 310, y: 25, width: 35, height: options?.wcLinkRect ? 20 : 35 }, poweredByUrl)
}

/**
 * Port of iOS drawWCImageCover (single-artwork White Cube cover):
 * template background, artwork image aspect-FILLED (cropped) into the band
 * between 13.4%-height top/bottom margins, uppercase title in the bottom band.
 */
export async function drawWCImageCover(
	doc: PDFDocument,
	page: PDFPage,
	title: string,
	imageJpeg: Uint8Array | null,
	templateBytes: Uint8Array | null,
	fonts: IFontSet
): Promise<void> {
	drawWhiteBackground(page)
	await drawTemplateBackground(doc, page, templateBytes)

	const width = pageWidth
	const height = pageHeight
	const padding = height * 0.134
	const imageAreaW = width
	const imageAreaH = height - 2 * padding

	if (imageJpeg) {
		// iOS aspectFillImage crops to the area, so the image cannot bleed into the bands
		const cropped = await cropFill(imageJpeg, imageAreaW / imageAreaH)
		const image = await embedImage(doc, cropped)
		drawImageInTopRect(page, image, 0, padding, imageAreaW, imageAreaH)
	}

	const titleHeight = height * 0.134
	drawRuns(
		page,
		runsFromText(title, {
			font: fonts.coverFont("wc"),
			size: fonts.coverFontSize("wc"),
			lineSpacing: 2,
			align: "center",
			color: black,
		}),
		{ x: padding, yTop: height - titleHeight, width: width - padding * 2, height: titleHeight },
		"center"
	)
}
